import generateChallenge from 'pkce-challenge'
import { config } from '../../config'

const createPkceData = async () => {
	const { code_challenge, code_verifier } = await generateChallenge()
	sessionStorage.setItem('pkce_code_verifier', code_verifier)
	return code_challenge
}

const generateState = () => {
	const state = crypto.randomUUID()
	sessionStorage.setItem('oauth_state', state)
	return state
}

export const getCodeVerifier = () => {
	return sessionStorage.getItem('pkce_code_verifier')
}

export const getState = () => {
	return sessionStorage.getItem('oauth_state')
}

export const clearAuthParams = () => {
	sessionStorage.removeItem('oauth_state')
	sessionStorage.removeItem('pkce_code_verifier')
	sessionStorage.removeItem('auth_email')
}

export const storeIdToken = (idToken: string) => {
	sessionStorage.setItem('okta_id_token', idToken)
}

export const getIdToken = () => {
	return sessionStorage.getItem('okta_id_token')
}

export const clearStorage = () => {
	clearAuthParams()
	sessionStorage.removeItem('okta_id_token')
	// Clear localStorage (where sessionStore persists user data)
	localStorage.removeItem('session-storage')
}

const beginLoginWithOkta = async (email: string, rememberMe: boolean): Promise<void> => {
	sessionStorage.setItem('auth_email', email)
	const state = generateState()
	const codeChallenge = await createPkceData()

	const authUrl = new URL(`${config.okta.issuer}/v1/authorize`)
	authUrl.searchParams.append('client_id', config.okta.clientId)
	authUrl.searchParams.append('response_type', 'code')
	authUrl.searchParams.append('scope', 'openid profile email offline_access')
	authUrl.searchParams.append('redirect_uri', `${window.location.origin}/auth/callback`)
	authUrl.searchParams.append('code_challenge', codeChallenge)
	authUrl.searchParams.append('code_challenge_method', 'S256')
	authUrl.searchParams.append('state', state)
	authUrl.searchParams.append('login_hint', email) // Pre-fill email in Okta
	authUrl.searchParams.append('remember_me', rememberMe ? 'true' : 'false')
	window.location.assign(authUrl.toString())
}

/**
 * Build the Okta logout URL to end the SSO session
 * This ensures users can switch accounts after logout
 */
export const getOktaLogoutUrl = (): string => {
	const logoutUrl = new URL(`${config.okta.issuer}/v1/logout`)
	logoutUrl.searchParams.append('client_id', config.okta.clientId)
	logoutUrl.searchParams.append('post_logout_redirect_uri', `${window.location.origin}/auth/login`)
	const idToken = getIdToken()
	if (idToken) {
		logoutUrl.searchParams.append('id_token_hint', idToken)
	}
	return logoutUrl.toString()
}

const beginLoginLocally = async (email: string, _rememberMe: boolean) => {
	sessionStorage.setItem('auth_email', email)
	const state = generateState()
	const codeChallenge = await createPkceData()
	window.location.href = `${window.location.origin}/auth/callback?code=LOCAL_DEV_CODE:${email}&state=${state}&code_verifier=${codeChallenge}`
}

/**
 * Begin the login process after email is entered
 * @param {string} email
 */
export const beginLogin = async (email: string, rememberMe: boolean) => {
	if (config.env === 'local' || config.env === 'dev' || config.env === 'demo') {
		return beginLoginLocally(email, rememberMe)
	}
	return beginLoginWithOkta(email, rememberMe)
}
